import { useState } from 'react'

// The 43 districts of Lima province (INEI), alphabetical. A free-text
// district is always allowed underneath this (the DB column is a plain
// string, here and on every business that reuses this component) -- this is
// a UI convenience to reduce typos/variants for the common case, not a new
// validation rule, so "Otro" + manual entry must stay available for
// anything outside Lima.
export const LIMA_DISTRICTS = [
  'Ancón', 'Ate', 'Barranco', 'Breña', 'Carabayllo', 'Chaclacayo', 'Chorrillos',
  'Cieneguilla', 'Comas', 'El Agustino', 'Independencia', 'Jesús María',
  'La Molina', 'La Victoria', 'Lima (Cercado)', 'Lince', 'Los Olivos',
  'Lurigancho', 'Lurín', 'Magdalena del Mar', 'Miraflores', 'Pachacámac',
  'Pucusana', 'Pueblo Libre', 'Puente Piedra', 'Punta Hermosa', 'Punta Negra',
  'Rímac', 'San Bartolo', 'San Borja', 'San Isidro', 'San Juan de Lurigancho',
  'San Juan de Miraflores', 'San Luis', 'San Martín de Porres', 'San Miguel',
  'Santa Anita', 'Santa María del Mar', 'Santa Rosa', 'Santiago de Surco',
  'Surquillo', 'Villa El Salvador', 'Villa María del Triunfo',
]
const OTHER_DISTRICT_SENTINEL = '__other__'

interface DistrictPickerProps {
  value: string
  onChange: (value: string) => void
  label?: string
}

/** Shared district field: a dropdown of Lima's 43 districts plus "Otro" with
 * manual text entry, used by every business's customer form (coffee,
 * FX Admin, ...). Lifted out of the coffee business's CustomerFormModal so a
 * second business didn't have to re-declare the same 43-item list. */
export default function DistrictPicker({ value, onChange, label = 'Distrito' }: DistrictPickerProps) {
  // Computed once at mount, same reasoning as the coffee form this was
  // extracted from: if the component remounts fresh per edit (parent's
  // `{showForm && <Modal/>}` pattern), a customer whose district isn't in
  // the Lima list correctly opens straight into "Otro" with their existing
  // value instead of silently discarding it.
  const [mode, setMode] = useState<'select' | 'other'>(
    value && !LIMA_DISTRICTS.includes(value) ? 'other' : 'select'
  )

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value
    if (next === OTHER_DISTRICT_SENTINEL) {
      setMode('other')
      // Reset so switching to "Otro" doesn't silently keep whatever Lima
      // district was previously selected.
      onChange('')
    } else {
      setMode('select')
      onChange(next)
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-espresso-700 mb-1">{label}</label>
      <select
        value={mode === 'other' ? OTHER_DISTRICT_SENTINEL : value}
        onChange={handleSelectChange}
        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
      >
        <option value="">Selecciona un distrito</option>
        {LIMA_DISTRICTS.map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
        <option value={OTHER_DISTRICT_SENTINEL}>Otro</option>
      </select>
      {mode === 'other' && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Escribe el distrito/ubicación"
          className="mt-2 w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          required
        />
      )}
    </div>
  )
}
